import Copy from "@/components/copy";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link2 } from "lucide-react";
import { useState, type FC } from "react";

/**
 * The partner invite card — the redesign of the old `canRefer` commission
 * block. It replaces the Total Commission / Commission Rate readout entirely:
 * the new design is a pure invite surface, code plus link, with no figures.
 *
 * Shared by the Dashboard and My Wallet grids, which differ only in how many
 * columns they hand it, so the span comes in through `className`.
 *
 * Paints from the Referrals screen's own tokens (`dark-referral-hero` and the
 * cyan `dark-referral-accent`), not the surrounding grid's `dark-card-2`, so
 * this card reads as the same surface as the hero inside the Referrals sheet.
 */
const ReferralInviteCard: FC<{ code?: string; className?: string }> = ({
  code = "",
  className,
}) => {
  const [linkCopied, setLinkCopied] = useState(false);

  /* Deliberately NOT `handleShare`: this block is desktop-only (`lg:` and up)
     and `navigator.share` is mobile-only, so the old "Share Your Link" button
     fell through to its `alert("Sharing not supported…")` branch on every
     desktop. The new label says "Copy link", so it copies. */
  const copyLink = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/signup?ref=${code}`,
    );
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 3000);
  };

  return (
    <div
      className={cn(
        "bg-dark-referral-hero text-dark-text-50 relative hidden w-full flex-col overflow-hidden rounded-2xl p-5 lg:flex",
        className,
      )}
    >
      {/* `pr-*` on the headline below reserves this art's width — a fixed
          reserve, because the art does not scale with the column. */}
      <img
        src="/images/gift.webp"
        alt=""
        aria-hidden
        className="pointer-events-none absolute -top-4 -right-4 w-32 select-none xl:w-40"
      />

      <h3 className="relative pr-24 text-2xl leading-tight font-bold text-balance xl:pr-28">
        Invite your friends and family to join{" "}
        <span className="text-dark-referral-accent">PayG8</span>
      </h3>

      {/* `mt-auto` pins the code group to the bottom of whatever height the
          grid row gives this card, so it lines up with the neighbouring
          wallet/cards panels rather than floating mid-card. */}
      <p className="text-dark-text-300 relative mt-auto pt-10 text-sm">
        Your referral code
      </p>

      {/* Figma gives this field's stroke the same gradient as its fill, which
          would render as no visible edge at all — but the mock shows a clear
          dashed rule. Kept on Stroke/3 dashed, which is what the mock reads
          as. Say if the stroke really is meant to disappear. */}
      <div className="bg-dark-referral-code border-dark-stroke-3 relative mt-2 flex h-11 items-center justify-center gap-2 rounded-lg border border-dashed px-3">
        <span className="text-dark-text-50 min-w-0 truncate font-semibold tracking-wide">
          {code}
        </span>
        <Copy text={code} variant="gradient" side="left" />
      </div>

      {/* `bg-transparent` kills the Button variant's background-COLOR;
          `bg-dark-referral-action` is a background-IMAGE, so it layers on top
          of that rather than fighting it. Figma gives no hover state, so the
          hover is an opacity shift, as elsewhere on this screen. */}
      <Button
        type="button"
        onClick={copyLink}
        className="bg-dark-referral-action border-dark-referral-action-stroke text-dark-text-50 relative mt-2.5 flex h-11 w-full items-center justify-center gap-2 rounded-lg border bg-transparent hover:cursor-pointer hover:opacity-90"
      >
        <Link2 className="size-4" />
        <span className="text-sm">
          {linkCopied ? "Link copied" : "Copy link"}
        </span>
      </Button>
    </div>
  );
};

export default ReferralInviteCard;
